/* eslint-disable react-refresh/only-export-components */
import { createContext, useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { initializePWABranding } from '../utils/pwaUtils';

// Import tenant configurations
import defaultTenant from '../../config/tenants/default.json';
import vikramauraTenant from '../../config/tenants/vikramaura-hospitals.json';
import shirdisaiTenant from '../../config/tenants/shirdisai.json';
import bayhillsTenant from '../../config/tenants/bayhills.json';

// Tenant configurations map
const TENANT_CONFIGS_DATA = {
  'default': defaultTenant,
  'vikramaura-hospitals': vikramauraTenant,
  'shirdisai': shirdisaiTenant,
  'bayhills': bayhillsTenant
};

// Domain to tenant ID mapping
const TENANT_DOMAIN_MAP = {
  'localhost': 'default',
  '127.0.0.1': 'default',
  'vikramaurahospitals.com': 'vikramaura-hospitals',
  'shirdisai.com': 'shirdisai',
  'bayhills.com': 'bayhills'
};

export const DEFAULT_TENANT = {
  ...defaultTenant,
  subdomain: '',
  // Legacy fields for backward compatibility - use tenant-specific logo files
  logo: '/default-logo.png',
  qrLogo: '/default-logo.png',
  contactEmail: defaultTenant.contact?.email || 'healthsnai@gmail.com',
  contactPhone: defaultTenant.contact?.phone || '+(1) 925 336 1097',
  address: defaultTenant.contact?.address || 'PO Box 2011 Dublin, CA 94568',
  mapsEmbedUrl: defaultTenant.maps?.embedUrl || '',
  googleMapsLink: defaultTenant.maps?.googleMapsLink || ''
};

// Tenant configurations map - synchronized with backend/config/tenants/
const TENANT_CONFIGS = TENANT_DOMAIN_MAP;

export function extractSubdomain(hostname) {
  if (!hostname) return '';
  const parts = hostname.split('.');
  if (parts.length <= 2) return '';
  return parts[0];
}

export function getTenantIdFromDomain(hostname) {
  if (!hostname) return 'default';
  
  // Remove port if present
  const cleanHostname = hostname.split(':')[0].toLowerCase();
  
  // Check exact domain match
  if (TENANT_CONFIGS[cleanHostname]) {
    return TENANT_CONFIGS[cleanHostname];
  }
  
  // Check if hostname ends with any configured domain
  for (const [domain, tenantId] of Object.entries(TENANT_CONFIGS)) {
    if (cleanHostname.endsWith(`.${domain}`) || cleanHostname === domain) {
      return tenantId;
    }
  }
  
  return 'default';
}

export function getTenantConfig(tenantId) {
  return TENANT_CONFIGS_DATA[tenantId] || TENANT_CONFIGS_DATA['default'];
}

export const resolveTenantFromHost = () => {
  try {
    // Check for VITE_TENANT_OVERRIDE environment variable first
    const tenantOverride = import.meta.env.VITE_TENANT_OVERRIDE;
    
    let tenantId = 'default';
    
    if (tenantOverride) {
      // Use override if set
      tenantId = tenantOverride;
      console.log(`[Tenant] Using VITE_TENANT_OVERRIDE: ${tenantId}`);
    } else {
      // Otherwise detect from domain
      const host = typeof window !== 'undefined' ? window.location.host : '';
      tenantId = getTenantIdFromDomain(host);
    }
    
    const sub = extractSubdomain(typeof window !== 'undefined' ? window.location.host : '');
    const tenantConfig = getTenantConfig(tenantId);
    
    return {
      ...tenantConfig,
      subdomain: sub,
      // Legacy fields for backward compatibility - use actual URLs from config
      logo: tenantConfig.branding?.logo || '/logo.png',
      qrLogo: tenantConfig.branding?.['qr-logo'] || tenantConfig.branding?.logo || '/qr-logo.png',
      contactEmail: tenantConfig.contact?.email || '',
      contactPhone: tenantConfig.contact?.phone || '',
      address: tenantConfig.contact?.address || '',
      mapsEmbedUrl: tenantConfig.maps?.embedUrl || '',
      googleMapsLink: tenantConfig.maps?.googleMapsLink || ''
    };
  } catch (e) {
    return DEFAULT_TENANT;
  }
};

const TenantContext = createContext({ tenant: DEFAULT_TENANT, setTenant: () => {}, loading: false });

export const TenantProvider = ({ children, initialTenant }) => {
  const [tenant, setTenant] = useState(() => {
    const initialTenantData = initialTenant || resolveTenantFromHost();
    return initialTenantData;
  });
  const [loading, setLoading] = useState(true);

  // Initialize PWA branding immediately on mount
  useEffect(() => {
    initializePWABranding(tenant).catch((e) => {
      console.warn('[Tenant] PWA branding init error:', e);
    });
  }, [tenant]);

  useEffect(() => {
    // Fetch tenant config from backend API
    const fetchTenantConfig = async () => {
      try {
        setLoading(true);

        // When a local override is active, the JSON file is authoritative — skip backend fetch
        const tenantOverride = import.meta.env.VITE_TENANT_OVERRIDE;
        if (tenantOverride) {
          console.log(`[Tenant] VITE_TENANT_OVERRIDE="${tenantOverride}" — using local JSON, skipping backend fetch`);
          if (tenant.branding) applyBranding(tenant.branding);
          return;
        }

        // Determine tenant ID from domain
        const host = typeof window !== 'undefined' ? window.location.host : '';
        const tenantId = getTenantIdFromDomain(host);
        console.log(`[Tenant] Detected tenant ID from domain "${host}": ${tenantId}`);

        const baseURL = import.meta.env.VITE_REACT_APP_BACKEND_BASEURL || 'http://localhost:4000';
        const response = await axios.get(`${baseURL}/api/v1/tenant/config`);

        if (response.data.success && response.data.tenant) {
          const backendTenant = response.data.tenant;
          console.log(`[Tenant] Loaded tenant from backend:`, backendTenant.name);

          // Merge backend config with existing tenant data
          const mergedTenant = {
            ...tenant,
            ...backendTenant,
            subdomain: tenant.subdomain,
            branding: { ...backendTenant.branding },
            logo: backendTenant.branding?.logo || tenant.logo || '/logo.png',
            qrLogo: backendTenant.branding?.['qr-logo'] || backendTenant.branding?.logo || tenant.qrLogo || '/qr-logo.png',
            contactEmail: backendTenant.contact?.email || tenant.contactEmail,
            contactPhone: backendTenant.contact?.phone || tenant.contactPhone,
            address: backendTenant.contact?.address || tenant.address,
            mapsEmbedUrl: backendTenant.maps?.embedUrl || tenant.mapsEmbedUrl || '',
            googleMapsLink: backendTenant.maps?.googleMapsLink || tenant.googleMapsLink || ''
          };

          setTenant(mergedTenant);
          if (backendTenant.branding) applyBranding(backendTenant.branding);
        }
      } catch (err) {
        console.error('Error fetching tenant config from backend:', err);
        // Continue with default/env-based tenant on error
      } finally {
        setLoading(false);
      }
    };

    fetchTenantConfig();

    // Also check localStorage override
    const ls = localStorage.getItem('tenantOverride');
    if (ls) {
      try { 
        const override = JSON.parse(ls);
        setTenant(prev => ({ ...prev, ...override }));
      } catch (e) { /* noop */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyBranding = (branding) => {
    // Apply CSS custom properties for theming
    if (branding.primaryColor) {
      document.documentElement.style.setProperty('--tenant-primary-color', branding.primaryColor);
    }
    if (branding.secondaryColor) {
      document.documentElement.style.setProperty('--tenant-secondary-color', branding.secondaryColor);
    }
  };

  const value = useMemo(() => ({ tenant, setTenant, loading }), [tenant, loading]);
  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};

TenantProvider.propTypes = {
  children: PropTypes.node,
  initialTenant: PropTypes.object,
};

export default TenantContext;
